// src/components/dashboard/TableroPDF.jsx
// PDF del Tablero de Indicadores (/tableros). Mismo criterio que los PDFs de
// Informe técnico (con y sin observaciones): se arma como TEXTO con react-pdf,
// con flujo y salto de página nativos. NADA se convierte a canvas para después
// "recortar en páginas": los únicos raster son los gráficos/mapas individuales
// (dataURLs que captura la página al momento de imprimir), y cada uno entra al
// documento como una <Image> atómica dentro del layout de texto, así react-pdf
// lo corre de página sin cortarlo por la mitad.
import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import RobotoRegular from '../../fonts/Roboto-Regular.ttf';
import RobotoBold from '../../fonts/Roboto-Bold.ttf';
import RobotoItalic from '../../fonts/Roboto-Italic.ttf';
import encabezadoA4 from '../informes/encabezado_A4.png';
import pieHojaA4 from '../informes/pie_hoja_A4.png';

// Misma fuente que el Informe técnico; TTF estáticos (la variable rompe fontkit).
Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoRegular, fontWeight: 400 },
        { src: RobotoBold, fontWeight: 700 },
        { src: RobotoItalic, fontWeight: 400, fontStyle: 'italic' },
    ],
});

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
        fontFamily: 'Roboto',
        fontSize: 10,
        color: '#333333',
        paddingTop: 84,      // lugar para el encabezado (imagen fija)
        paddingBottom: 122,  // lugar para el pie (imagen fija)
        paddingHorizontal: 57,
    },
    encabezado: {
        position: 'absolute',
        top: 24,
        left: 57,
        right: 57,
    },
    encabezadoImg: {
        width: '100%',
    },
    pie: {
        position: 'absolute',
        bottom: 24,
        left: 57,
        right: 57,
    },
    pieImg: {
        width: '100%',
    },
    numeroPagina: {
        textAlign: 'center',
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
    titulo: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.5,
        marginBottom: 2,
    },
    subtitulo: {
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'center',
        color: '#555555',
        marginBottom: 14,
    },
    fecha: {
        fontSize: 9.5,
        color: '#666666',
        textAlign: 'right',
        marginBottom: 6,
    },
    separador: {
        borderBottomWidth: 1,
        borderBottomColor: '#d1d5db',
        marginTop: 10,
        marginBottom: 8,
    },
    kpisGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 6,
    },
    kpiCard: {
        width: '49%',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        paddingVertical: 8,
        paddingHorizontal: 10,
        marginBottom: 8,
        textAlign: 'center',
    },
    kpiLabel: {
        fontSize: 9,
        color: '#6b7280',
        marginBottom: 2,
        textTransform: 'uppercase',
    },
    kpiValor: {
        fontSize: 16,
        fontWeight: 700,
        color: '#2563eb',
    },
    gaugeWrap: {
        alignItems: 'center',
        marginTop: 4,
    },
    gaugeImg: {
        width: 140,
        height: 140,
    },
    seccionTitulo: {
        fontSize: 12,
        fontWeight: 700,
        marginTop: 12,
        marginBottom: 4,
    },
    chartImg: {
        width: '100%',
    },
    leyenda: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 4,
        marginBottom: 6,
    },
    leyendaItem: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 10,
        marginBottom: 3,
    },
    leyendaSwatch: {
        width: 10,
        height: 10,
        marginRight: 3,
    },
    leyendaTexto: {
        fontSize: 8,
        color: '#555555',
    },
    tabla: {
        marginTop: 2,
        marginBottom: 10,
    },
    tablaTitulo: {
        fontSize: 10,
        fontWeight: 700,
        color: '#374151',
        marginTop: 2,
        marginBottom: 4,
    },
    tablaCabecera: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#9ca3af',
        paddingVertical: 4,
        backgroundColor: '#f3f4f6',
    },
    tablaFila: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 3,
    },
    tablaCeldaAnio: {
        width: 40,
        fontWeight: 700,
        fontSize: 8.5,
        paddingHorizontal: 4,
    },
    tablaCelda: {
        flex: 1,
        fontSize: 8.5,
        paddingHorizontal: 4,
    },
    tablaCeldaNum: {
        width: 68,
        textAlign: 'right',
        fontSize: 8.5,
        paddingHorizontal: 4,
    },
    filtroItem: {
        marginBottom: 2,
    },
    filtroLabel: {
        fontWeight: 700,
    },
});

// Leyenda vectorial de los mapas (misma escala de colores y rangos que la
// pantalla). Se dibuja con rectángulos de color: no depende del raster del
// mapa, así se imprime nítida y no se pierde al capturar el canvas.
const LeyendaMapa = ({ rangos }) => {
    if (!rangos || rangos.length < 5) return null;
    const items = [
        { color: '#e2e8f0', label: '0' },
        { color: '#bae6fd', label: `1 - ${rangos[0]}` },
        { color: '#7dd3fc', label: `${rangos[0] + 1} - ${rangos[1]}` },
        { color: '#38bdf8', label: `${rangos[1] + 1} - ${rangos[2]}` },
        { color: '#0284c7', label: `${rangos[2] + 1} - ${rangos[3]}` },
        { color: '#0369a1', label: `${rangos[3] + 1} +` },
    ];
    return (
        <View style={styles.leyenda}>
            {items.map((it, i) => (
                <View key={i} style={styles.leyendaItem}>
                    <View style={[styles.leyendaSwatch, { backgroundColor: it.color }]} />
                    <Text style={styles.leyendaTexto}>{it.label}</Text>
                </View>
            ))}
        </View>
    );
};

// Formateo de montos y porcentajes IDÉNTICO al de los gráficos anuales
// (GraficoRecursosPorTipoAnual / GraficoSubsidiosAnual), para que la tabla
// muestre exactamente los mismos valores que las barras.
const formatPesos = (valor) => {
    if (valor >= 1000000000) return `$${(valor / 1000000000).toFixed(2)}B`;
    if (valor >= 1000000) return `$${(valor / 1000000).toFixed(2)}M`;
    return `$${Math.round(valor).toLocaleString('es-AR')}`;
};
const formatPorc = (valor) => `${valor.toFixed(1).replace('.', ',')}%`;

// Rango de años del eje X de los gráficos anuales (mismo que en pantalla).
const ANIOS_MOSTRADOS = Array.from({ length: 11 }, (_, i) => 2016 + i);

// Construye las filas de la tabla anual a partir de los datos del backend
// ({ anio, municipio, ong }). `tipo`: 'recursos' (montos) o 'proyectos' (cant).
const construirFilasAnual = (data, tipo) => {
    const porAnio = new Map((data || []).map(item => [Number(item.anio), item]));
    return ANIOS_MOSTRADOS.map(anio => {
        const item = porAnio.get(anio) || {};
        const m = Number(item.municipio) || 0;
        const o = Number(item.ong) || 0;
        const total = m + o;
        const pctM = total > 0 ? (m / total) * 100 : 0;
        const pctO = total > 0 ? (o / total) * 100 : 0;
        if (tipo === 'recursos') {
            return {
                anio,
                municipio: `${formatPesos(m)} (${formatPorc(pctM)})`,
                ong: `${formatPesos(o)} (${formatPorc(pctO)})`,
                total: formatPesos(total),
            };
        }
        return {
            anio,
            municipio: `${m} (${formatPorc(pctM)})`,
            ong: `${o} (${formatPorc(pctO)})`,
            total: `${total} proyectos`,
        };
    });
};

// Tabla vectorial con los valores por año: complementa al gráfico anual cuyos
// labels (10px) pierden legibilidad al entrar en el PDF a 481pt de ancho.
// Texto puro de react-pdf: nítido y legible siempre.
const TablaAnual = ({ titulo, filas }) => {
    if (!filas || filas.length === 0) return null;
    return (
        <View style={styles.tabla}>
            <Text style={styles.tablaTitulo}>{titulo}</Text>
            <View style={styles.tablaCabecera}>
                <Text style={styles.tablaCeldaAnio}>Año</Text>
                <Text style={styles.tablaCelda}>Municipio</Text>
                <Text style={styles.tablaCelda}>ONG/Univ.</Text>
                <Text style={styles.tablaCelda}>Total</Text>
            </View>
            {filas.map(f => (
                <View key={f.anio} style={styles.tablaFila}>
                    <Text style={styles.tablaCeldaAnio}>{f.anio}</Text>
                    <Text style={styles.tablaCelda}>{f.municipio}</Text>
                    <Text style={styles.tablaCelda}>{f.ong}</Text>
                    <Text style={styles.tablaCelda}>{f.total}</Text>
                </View>
            ))}
        </View>
    );
};

// Filas de la tabla de Proyectos por Partido. Mismo criterio que el gráfico: en
// vista "Top 15" se listan los 15 mayores; en vista "Todos", todos los partidos
// (que es justo lo que el gráfico también muestra cuando se quita el Top 15).
// El % se calcula sobre el total de proyectos del universo completo.
const construirFilasPartidos = (data, mostrarTop15) => {
    const ordenados = [...(data || [])]
        .map(p => ({ partido: p.partido || 'Sin partido', cantidad: Number(p.cantidad) || 0 }))
        .sort((a, b) => b.cantidad - a.cantidad);
    const total = ordenados.reduce((acc, p) => acc + p.cantidad, 0);
    const filas = mostrarTop15 ? ordenados.slice(0, 15) : ordenados;
    return filas.map(p => ({
        partido: p.partido,
        cantidad: `${p.cantidad}`,
        porcentaje: total > 0 ? formatPorc((p.cantidad / total) * 100) : '—',
    }));
};

// Tabla vectorial de Proyectos por Partido: complementa al gráfico de barras
// (en vista "Todos" las barras quedan finas y sus labels se pisan). Texto puro
// de react-pdf, nítido y legible siempre.
const TablaPartidos = ({ titulo, filas }) => {
    if (!filas || filas.length === 0) return null;
    return (
        <View style={styles.tabla}>
            <Text style={styles.tablaTitulo}>{titulo}</Text>
            <View style={styles.tablaCabecera}>
                <Text style={styles.tablaCelda}>Partido</Text>
                <Text style={styles.tablaCeldaNum}>Proyectos</Text>
                <Text style={styles.tablaCeldaNum}>% del total</Text>
            </View>
            {filas.map((f, i) => (
                <View key={`${f.partido}-${i}`} style={styles.tablaFila}>
                    <Text style={styles.tablaCelda}>{f.partido}</Text>
                    <Text style={styles.tablaCeldaNum}>{f.cantidad}</Text>
                    <Text style={styles.tablaCeldaNum}>{f.porcentaje}</Text>
                </View>
            ))}
        </View>
    );
};

// Altura tope del mapa de RMBA en el PDF: en pantalla las dos tarjetas miden
// lo mismo (550px) y se perciben igual; la de RMBA es retrato, así que si se
// dibujara a ancho completo quedaría gigante. Se la limita a esta altura.
const MAPA_ALTURA = 330;

// Sección de un mapa: mismo criterio que SeccionGrafico, con ancho calculado
// por la proporción real del mapa. `expand` (pt) STIRA la sección más allá del
// ancho útil por ambos lados (para que un mapa apaisado gane ancho); `maxAlto`
// limita la altura. El mapa de la provincia va con expand = margen de hoja y un
// tope alto pero con margen para que la leyenda entre en la MISMA hoja (a 580
// la sección superaba el alto útil y la leyenda saltaba sola a la siguiente).
const SeccionMapa = ({ titulo, src, ratio, rangos, expand = 0, maxAlto = MAPA_ALTURA }) => {
    if (!src) return null;
    const anchoMax = 481 + 2 * expand;
    let w = anchoMax;
    let h = null;
    if (ratio) {
        h = w / ratio;
        if (h > maxAlto) {
            h = maxAlto;
            w = h * ratio;
        }
    }
    return (
        <View wrap={false} style={[expand && { marginHorizontal: -expand }, { marginTop: 10, alignItems: 'center' }]}>
            <Text style={styles.seccionTitulo}>{titulo}</Text>
            <Image style={h ? { width: w, height: h } : { width: '100%' }} src={src} />
            <LeyendaMapa rangos={rangos} />
        </View>
    );
};

// Una sección de gráfico: título + imagen, agrupados para no separarse entre
// páginas (wrap={false}: si no entra el bloque completo, salta entero a la
// siguiente hoja, nunca queda el título solo). Si la imagen no llegó (chart sin
// datos / plot sin renderizar), la sección se omite completa. Un `footer`
// opcional se dibuja debajo de la imagen (p.ej. la leyenda del mapa). `expand`
// (en pt) STIRA la sección más allá del ancho útil por ambos lados, para que
// un gráfico apaisado (el mapa de la provincia) aproveche más la hoja: a
// ancho completo quedaba chico y dejaba mucho blanco debajo.
const SeccionGrafico = ({ titulo, src, footer = null, expand = 0 }) =>
    src ? (
        <View wrap={false} style={[expand && { marginHorizontal: -expand }, { marginTop: 10 }]}>
            <Text style={styles.seccionTitulo}>{titulo}</Text>
            <Image style={styles.chartImg} src={src} />
            {footer}
        </View>
    ) : null;

const TableroPDF = ({ kpis = {}, filtrosAplicados = [], imagenes = {}, rangosLeyenda = null, mapaProvinciaRatio = null, mapaConurbanoRatio = null, recursosAnualData = null, proyectosAnualData = null, partidosData = null, partidosMostrarTop15 = true, fecha }) => {
    const {
        totalObras = 0,
        presupuestoTotal = 0,
        municipiosAlcanzados = 0,
        beneficiarios = 0,
    } = kpis;

    const monto = `$${(presupuestoTotal / 1000000).toFixed(2)}M`;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.encabezado}>
                    <Image style={styles.encabezadoImg} src={encabezadoA4} />
                </View>

                <Text style={styles.titulo}>Tablero de Indicadores</Text>
                <Text style={styles.subtitulo}>Dirección Provincial de Intervenciones Complementarias</Text>
                <Text style={styles.fecha}>Fecha de emisión: {fecha}</Text>

                {/* Filtros aplicados (arriba, en la primera hoja) */}
                {filtrosAplicados.length > 0 && (
                    <View>
                        <Text style={styles.seccionTitulo}>Filtros aplicados</Text>
                        <View style={styles.separador} />
                        {filtrosAplicados.map((f, i) => (
                            <Text key={i} style={styles.filtroItem}>
                                <Text style={styles.filtroLabel}>{f.clave}: </Text>
                                {f.valor}
                            </Text>
                        ))}
                    </View>
                )}

                {/* KPIs */}
                <View style={styles.kpisGrid}>
                    <View style={styles.kpiCard}>
                        <Text style={styles.kpiLabel}>Total Proyectos</Text>
                        <Text style={styles.kpiValor}>{totalObras}</Text>
                    </View>
                    <View style={styles.kpiCard}>
                        <Text style={styles.kpiLabel}>Monto Conveniado</Text>
                        <Text style={styles.kpiValor}>{monto}</Text>
                    </View>
                    <View style={styles.kpiCard}>
                        <Text style={styles.kpiLabel}>Municipios Alcanzados</Text>
                        <Text style={styles.kpiValor}>{municipiosAlcanzados}</Text>
                    </View>
                    <View style={styles.kpiCard}>
                        <Text style={styles.kpiLabel}>Beneficiarios</Text>
                        <Text style={styles.kpiValor}>{beneficiarios}</Text>
                    </View>
                </View>

                {imagenes.gauge && (
                    /* el gauge ahora se captura desde un contenedor CUADRADO
                       (w-40 h-40), así la imagen nace 1:1 y la caja 140×140
                       respeta las proporciones */
                    <View style={styles.gaugeWrap}>
                        <Image style={styles.gaugeImg} src={imagenes.gauge} />
                    </View>
                )}

                {/* KPIs → gráficos */}
                <SeccionGrafico titulo="Proyectos por Partido" src={imagenes.partidos} />
                <TablaPartidos
                    titulo={partidosMostrarTop15
                        ? 'Detalle por partido (15 mayores y % sobre el total)'
                        : 'Detalle por partido (todos y % sobre el total)'}
                    filas={construirFilasPartidos(partidosData, partidosMostrarTop15)}
                />
                <SeccionGrafico titulo="Proyectos por Programa" src={imagenes.tortaProgramas} />
                <SeccionGrafico titulo="Proyectos por Estado" src={imagenes.tortaEstados} />
                <SeccionMapa
                    titulo="Distribución Provincial de Obras"
                    src={imagenes.mapaProvincia}
                    ratio={mapaProvinciaRatio}
                    rangos={rangosLeyenda}
                    expand={57}
                    maxAlto={540}
                />
                <SeccionMapa
                    titulo="Región Metropolitana (RMBA)"
                    src={imagenes.mapaConurbano}
                    ratio={mapaConurbanoRatio}
                    rangos={rangosLeyenda}
                />
                <SeccionGrafico
                    titulo="Porcentaje de recursos transferidos por Tipo de subsidio por Año"
                    src={imagenes.recursosAnual}
                />
                <TablaAnual
                    titulo="Detalle por año (montos y % sobre el total)"
                    filas={construirFilasAnual(recursosAnualData, 'recursos')}
                />
                <SeccionGrafico
                    titulo="Evolución del Porcentaje de los Proyectos por Tipo de subsidio por Año"
                    src={imagenes.proyectosAnual}
                />
                <TablaAnual
                    titulo="Detalle por año (cantidad de proyectos y % sobre el total)"
                    filas={construirFilasAnual(proyectosAnualData, 'proyectos')}
                />

                <View fixed style={styles.pie}>
                    <Image style={styles.pieImg} src={pieHojaA4} />
                    <Text render={({ pageNumber }) => `-${pageNumber}-`} style={styles.numeroPagina} />
                </View>
            </Page>
        </Document>
    );
};

export default TableroPDF;